/**
 * Sessions dashboard section: tabbed list with create/edit slide panel.
 */

import * as React from 'react';
import { SessionsDataTable } from '@/components/sessions/remote/SessionsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { Select } from '@/components/forms/Select';
import { CreateSessionView } from '@/components/sessions/remote/CreateSessionView';
import { EditSessionView } from '@/components/sessions/remote/EditSessionView';
import { SelectSessionTypeModal } from '@/components/sessions/remote/SelectSessionTypeModal';
import { useQueryClient } from '@tanstack/react-query';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import type { SessionTab } from '@/types/remote/session.types';
import {
  SESSION_REMOTE_TAB_COLORS as TAB_COLORS,
  SESSION_REMOTE_TYPE_CONFIG as SESSION_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import { SESSION_TAB_LABELS } from '@/constants/shared-catalog';
import { getSessionTypesForTab, toSessionTab } from '@/utils/common-utils';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link SessionsSection}. */
interface SessionsSectionProps {
  layoutWidth?: ElementWidth;
}

/** Session type tabs shown in the remote dashboard widget. */
const TAB_ORDER: SessionTab[] = [
  'tutoring',
  'information-meeting',
  'welcome-meeting',
  'field-event',
];

/** Dashboard widget: remote sessions list with type picker. */
export const SessionsSection: React.FC<SessionsSectionProps> = ({ layoutWidth = 'full' }) => {
  const queryClient = useQueryClient();
  const {
    canCreate,
    canEditRow,
    canCancelRow,
    canExportCsv,
    canViewReportTutoring,
    canViewReportInformationMeeting,
    canViewReportWelcome,
    canViewReportFieldEvent,
    canJoinWelcomeMeeting,
    canJoinInformationMeeting,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('sessions');
  const { elements } = useInterfaceElements('sessions');
  const { openRowId, openRow, closeRow } = useDetailsView();

  const [activeTab, setActiveTab] = React.useState<SessionTab>('tutoring');
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedSessionType, setSelectedSessionType] = React.useState<SessionTab | null>(null);
  const [editSessionId, setEditSessionId] = React.useState<string | null>(null);
  const [syncOpen, setSyncOpen] = React.useState(false);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  const allowedTabIds = React.useMemo((): SessionTab[] => {
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const otherTabs: SessionTab[] =
      tabElements.length === 0
        ? ['tutoring', 'information-meeting', 'welcome-meeting', 'field-event']
        : tabElements
            .map((el) => toSessionTab(el.elementId))
            .filter((id): id is SessionTab => id !== null && id !== 'all');
    const sorted = [...otherTabs].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
    return sorted;
  }, [elements]);

  /**
   * Types creatable from action-create-* RBAC elements (per-type).
   * Falls back to allowedTabIds when no action-create-* elements exist.
   */
  const creatableTabIds = React.useMemo((): SessionTab[] => {
    const TAB_IDS = new Set<SessionTab>([
      'tutoring',
      'information-meeting',
      'welcome-meeting',
      'field-event',
    ]);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is SessionTab => TAB_IDS.has(id as SessionTab));
    }
    return allowedTabIds.filter((id) => id !== 'all') as SessionTab[];
  }, [creatableTypesRaw, allowedTabIds]);

  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      setSelectedSessionType(creatableTabIds[0]);
      setEditSessionId(null);
      closeRow();
    } else {
      setTypeModalOpen(true);
      setEditSessionId(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  const handleFormSuccess = React.useCallback(() => {
    setEditSessionId(null);
    setSelectedSessionType(null);
    void queryClient.invalidateQueries({ queryKey: ['sessions-remote'] });
  }, [queryClient]);

  const tabItems = React.useMemo(
    () =>
      allowedTabIds.map((id) => {
        const typesForTab = getSessionTypesForTab(id);
        const firstType = typesForTab[0]?.type;
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;
        return {
          id,
          label: SESSION_TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  const tabsNode = React.useMemo(
    () => (
      <Select
        value={activeTab}
        onChange={(id) => setActiveTab(id as typeof activeTab)}
        options={tabItems.map((tab) => ({
          value: tab.id,
          label: tab.label,
          icon: tab.icon,
          color: tab.color,
        }))}
        size="sm"
      />
    ),
    [tabItems, activeTab],
  );

  return (
    <>
      <SessionsDataTable
        activeTab={activeTab}
        allowedTabIds={allowedTabIds}
        expandedRowId={openRowId}
        onCloseDetails={closeRow}
        onEditSession={(id) => {
          setEditSessionId(id);
          setSelectedSessionType(null);
          closeRow();
        }}
        onView={openRow}
        enableFilters={true}
        isSection={true}
        layoutWidth={layoutWidth}
        pageActions={{
          canEditRow,
          canCancelRow,
          canExportCsv,
          canViewReportTutoring,
          canViewReportInformationMeeting,
          canViewReportWelcome,
          canViewReportFieldEvent,
          canJoinWelcomeMeeting,
          canJoinInformationMeeting,
        }}
        showCreateButton={canCreate}
        onCreateSession={handleCreateClick}
        toolbarLeft={tabsNode}
        toolbarExtra={
          CALENDAR_SYNC_ENABLED ? (
            <Button
              preset="section-action"
              icon={RefreshCw}
              tooltipText="Synchroniser"
              aria-label="Synchroniser"
              onClick={handleOpenSync}
            />
          ) : undefined
        }
      />

      <SelectSessionTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={(type) => {
          setSelectedSessionType(type);
          setTypeModalOpen(false);
          closeRow();
        }}
        allowedTabIds={creatableTabIds}
      />

      <SlidePanel
        isOpen={selectedSessionType !== null}
        onClose={() => setSelectedSessionType(null)}
      >
        {selectedSessionType && (
          <CreateSessionView
            sessionType={selectedSessionType}
            onClose={() => setSelectedSessionType(null)}
            onSuccess={handleFormSuccess}
          />
        )}
      </SlidePanel>

      <SlidePanel isOpen={editSessionId !== null} onClose={() => setEditSessionId(null)}>
        {editSessionId && (
          <EditSessionView
            key={editSessionId}
            sessionId={editSessionId}
            onClose={() => setEditSessionId(null)}
            onSuccess={handleFormSuccess}
          />
        )}
      </SlidePanel>
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal open={syncOpen} onClose={handleCloseSync} lockedTypes={['session']} />
      ) : null}
    </>
  );
};
